import {useEffect, useState} from 'react';

import {getCanvasDefinitionPreviewUrl} from './network/GetCanvasDefinitionPreview';
import {getCanvasVersionPreviewUrl} from './network/GetCanvasVersionPreview';
import {getPreviewErrorMessage} from './previewErrors';

export type CanvasExpandedPreviewTarget =
  | {canvasDefinitionId: string; canvasVersionId?: undefined}
  | {canvasVersionId: string; canvasDefinitionId?: undefined};

export interface UseCanvasExpandedPreviewUrlResult {
  signedUrl: string | null;
  isLoading: boolean;
  errorMessage: string | null;
}

export default function useCanvasExpandedPreviewUrl(
  target: CanvasExpandedPreviewTarget,
  refreshToken: number = 0
): UseCanvasExpandedPreviewUrlResult {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {canvasDefinitionId, canvasVersionId} = target;

  useEffect(() => {
    let isCancelled = false;

    setIsLoading(true);
    setErrorMessage(null);
    setSignedUrl(null);

    const fetchPreviewUrl =
      canvasVersionId != null
        ? getCanvasVersionPreviewUrl(canvasVersionId)
        : getCanvasDefinitionPreviewUrl(canvasDefinitionId);

    fetchPreviewUrl
      .then((response) => {
        if (!isCancelled) {
          setSignedUrl(response.signedUrl);
        }
      })
      // eslint-disable-next-line benchlint/always-throw-from-catch -- expected API errors are shown inline
      .catch((error) => {
        if (!isCancelled) {
          setErrorMessage(getPreviewErrorMessage(error));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [canvasDefinitionId, canvasVersionId, refreshToken]);

  return {signedUrl, isLoading, errorMessage};
}
